/**
 * MoralOnboardingFlow — esqueleto del flujo de onboarding Persona Moral
 * (F3.1 plan Persona Moral). Implementa la interfaz `OnboardingFlow` (F1).
 *
 * Por ahora es el ESQUELETO: declara los steps, paths, bloques BE y el mapeo
 * de next_step. La UI de cada step se construye en F3.2+ (account_access) y
 * F4-F6. El flow se registra siempre en el registry, pero solo se resuelve
 * cuando partyType=moral — que requiere el flag `onboarding_moral_enabled` on.
 *
 * Mapeo de bloques BE ↔ steps FE: ver docs/plans/persona-moral/03-screens-and-flow.md.
 */
import type { OnboardingFlow, OnboardingFlowStep } from '@tankef/core';

const steps: ReadonlyArray<OnboardingFlowStep> = [
  // Bloque account_access (screens 01–08): una sola pantalla, card stack.
  {
    id: 'access',
    path: 'access',
    i18nKey: 'onboardingMoral:accountAccess',
    block: 'account_access',
  },
  // Bloque company_docs (screen 09), partido en tres pantallas (1/3 → 3/3).
  {
    id: 'company_docs',
    path: 'company-docs',
    i18nKey: 'onboardingMoral:companyDocuments',
    block: 'company_docs',
  },
  // 2/3 y 3/3: estructura accionaria y organizacional.
  //
  // ⚠️ CONFLICTO CONOCIDO CON EL BACKEND (2026-08-11). Este orden es el del
  // diseño y es una decisión de producto, pero:
  //
  //   - `company/shareholder-structure` exige que la sesión tenga party PM, y
  //     ese party lo crea `company/data`, que aquí va DESPUÉS. Mientras el BE
  //     no relaje el requisito, guardar la accionaria devuelve
  //     ONB_SESSION_004 (correlation_id de ejemplo:
  //     bcbc5796-34ef-40f9-9767-313050c8ec40).
  //   - El BE ubica estos steps en `kyb_validation`: tras guardar la
  //     accionaria su `next_step` es `{kyb_validation, organigrama}`. Por eso
  //     `BACKEND_STEP_ALIASES` los traduce — el bloque FE y el del BE difieren
  //     a propósito.
  //
  // Antes de reordenarlos «para arreglar el 422», confirmar con producto: ya se
  // movieron una vez y se revirtieron.
  {
    id: 'company_structure',
    path: 'company-structure',
    i18nKey: 'onboardingMoral:companyStructure',
    block: 'company_docs',
  },
  {
    id: 'org_structure',
    path: 'company-org-structure',
    i18nKey: 'onboardingMoral:companyStructure',
    block: 'company_docs',
  },
  // Datos de identificación del propietario real, persona física (diseño
  // 2026-08-18). Es la ÚNICA pantalla de propietarios reales del flujo PM: la
  // vieja declaración de beneficiarios —porcentajes y tipo de control— se
  // retiró. Su bloque BE está por definirse; se ubica en el de la estructura
  // porque es donde el diseño la coloca.
  {
    id: 'beneficial_owner_identity',
    path: 'propietario-real',
    i18nKey: 'onboardingMoral:beneficialOwnerIdentity',
    block: 'company_docs',
  },
  // SCIAN (GAP 1, #412): actividad económica. Con el orden de 2026-08-18 ABRE el
  // bloque company_data (antes lo cerraba, tras company_address).
  // Restaurada (2026-08-05): el motor del BE la sigue emitiendo con
  // `required: true` en `moral_v1.yml`, así que sin ella el bloque company_data
  // nunca cierra. Ver docs/plans/persona-moral/09-fe-gaps-vs-412-2026-06-08.md.
  {
    id: 'scian',
    path: 'company-scian',
    i18nKey: 'onboardingMoral:companyScian',
    block: 'company_data',
  },
  // Bloque company_data (screens 10–11), ahora precedido por SCIAN.
  {
    id: 'company_data',
    path: 'company-data',
    i18nKey: 'onboardingMoral:companyData',
    block: 'company_data',
  },
  {
    id: 'company_address',
    path: 'company-address',
    i18nKey: 'onboardingMoral:companyData',
    block: 'company_data',
  },
  // Origen de fondos (GAP 2, #412) — "perfil transaccional" en el orden de
  // 2026-08-18: pasa a ir tras company_address, antes del KYB y del KYC del
  // representante. Antes iba tras beneficial_owners.
  {
    id: 'origin_of_funds',
    path: 'origin-of-funds',
    i18nKey: 'onboardingMoral:originOfFunds',
    block: 'origin_of_funds',
  },
  // Bloque legal_rep_kyc (screens 13–17): reusa los componentes KYC de PF con
  // la variante `legal_rep` (GAP 3 #412). INE frente+reverso se capturan en una
  // sola pantalla (igual que PF), por eso no hay step `legal_rep_ine_back`.
  {
    id: 'legal_rep_consent',
    path: 'legal-rep-consent',
    i18nKey: 'onboardingMoral:legalRepresentatives',
    block: 'legal_rep_kyc',
  },
  {
    id: 'legal_rep_ine_front',
    path: 'legal-rep-ine-front',
    i18nKey: 'onboardingMoral:legalRepresentatives',
    block: 'legal_rep_kyc',
  },
  {
    id: 'legal_rep_selfie',
    path: 'legal-rep-selfie',
    i18nKey: 'onboardingMoral:legalRepresentatives',
    block: 'legal_rep_kyc',
  },
  // Bloque legal_rep_data (screen 18).
  {
    id: 'legal_rep_data',
    path: 'legal-rep-data',
    i18nKey: 'onboardingMoral:legalRepresentatives',
    block: 'legal_rep_data',
  },
  // Domicilio del representante legal. Es la pantalla que CIERRA
  // `data_confirmation_admin`: ese step se completa con `kyc/confirm-data`,
  // que recibe identidad + domicilio, y `legal_rep_data` sólo manda
  // `legal-representatives` (poder y política de firma). Sin esta pantalla el
  // bloque `kyc_admin` se quedaba abierto.
  {
    id: 'legal_rep_address',
    path: 'legal-rep-address',
    i18nKey: 'onboardingMoral:legalRepAddress',
    block: 'legal_rep_data',
  },
  // Declaración PEP del representante (GAP 6, #412): va tras
  // el KYC del representante. Su `depends_on` en `moral_v1.yml` es
  // `beneficial_owners_declaration`, que ahora se captura mucho antes (en la
  // pantalla de identificación del propietario real).
  // Restaurada (2026-08-05): el motor del BE sigue emitiendo `pep_declaration_pm`
  // con `required: true`, así que sin la pantalla el bloque kyb_validation no
  // cierra. Ver docs/plans/persona-moral/09-fe-gaps-vs-412-2026-06-08.md.
  {
    id: 'pep_declaration',
    path: 'pep-declaration',
    i18nKey: 'onboardingMoral:pepDeclaration',
    block: 'pep_declaration',
  },
  // Espera de revisión KYB (GAP 4, #412): AASM pending_legal_review. Va ANTES
  // del cierre y no al final: así lo modela el BE —entra a `signing` DESPUÉS de
  // `pending_legal_review`, y `moral_v1.yml` ordena `privacy_notice_acceptance
  // → contracts_signature_pm` dentro de ese estado—. Moverla después de la
  // firma exigiría cambiarlo del lado del BE.
  {
    id: 'legal_review_wait',
    path: 'legal-review-wait',
    i18nKey: 'onboardingMoral:legalReviewWait',
    block: 'legal_review',
  },
  // Aviso de privacidad PM (GAP 5a, #412): bloque closing, ANTES de la firma.
  {
    id: 'privacy_notice',
    path: 'privacy-notice',
    i18nKey: 'onboardingMoral:privacyNotice',
    block: 'contracts_signature',
  },
  // Firma de contratos PM (GAP 5, #412): AASM signing. Reusa ContractPage.
  {
    id: 'contracts_pm',
    path: 'contracts',
    i18nKey: 'onboardingMoral:contracts',
    block: 'contracts_signature',
  },
  // Cierre (screen 20, modal CLABE + WS account.pm_provisioned).
  { id: 'finalize', path: 'finalize', i18nKey: 'onboardingMoral:closing', block: 'finalize' },
];

const byId = new Map(steps.map(s => [s.id, s]));
const byPath = new Map(steps.map(s => [s.path, s]));

/**
 * Traducción de claves de step del BE (`moral_v1.yml`) → ids de step FE, donde
 * difieren (#4). El motor de flujo del BE emite el `key:` del yml regulatorio
 * (p. ej. `scian_activity`, `pep_declaration_pm`); el FE mantiene ids propios
 * (más cortos o agrupados). Sin esta traducción, `mapBackendStep` no resolvería
 * un `next_step` dirigido por el BE en resume/recovery. Los steps de documentos
 * del BE (`company_csf`, `acta_*`) caen al FE `company_docs`, que agrupa todas
 * las subidas. Ver `docs/plans/persona-moral/09-fe-gaps-vs-412-2026-06-08.md`.
 */
const BACKEND_STEP_ALIASES: Record<string, string> = {
  // bloque company_data
  company_csf: 'company_docs',
  company_data_confirmation: 'company_data',
  scian_activity: 'scian',
  // bloque kyb_validation. Las pantallas de datos del acta y del poder notarial
  // SALIERON del flujo (2026-08-18): ambos documentos se entregan en la sección
  // Empresa de Documentación. Los alias se conservan como red — si el motor
  // sigue emitiendo esos steps, el usuario aterriza en Documentación en vez de
  // caer al fallback de "Crear acceso".
  acta_constitutiva: 'company_docs',
  acta_data_confirmation: 'company_docs',
  poder_notarial: 'company_docs',
  estructura_accionaria: 'company_structure',
  organigrama: 'org_structure',
  // La declaración de propietarios reales del BE aterriza en la pantalla de
  // identificación (2026-08-18). La pantalla vieja de beneficiarios —la de
  // porcentajes y tipo de control— salió del flujo PM.
  beneficial_owners_declaration: 'beneficial_owner_identity',
  pep_declaration_pm: 'pep_declaration',
  // La re-confirmación del domicilio SALIÓ del flujo (2026-08-31): el
  // `kyc/confirm-data` del representante ya verifica su domicilio, y el de la
  // empresa se capturó en `company_address`, pasos antes. El alias se conserva
  // como red: si el motor sigue emitiendo el step, el usuario aterriza en la
  // espera de revisión en vez de caer al fallback de "Crear acceso".
  company_address_verify: 'legal_review_wait',
  // bloque kyc_admin (identidad del representante legal). El BE nombra estos
  // steps con sufijo `_admin`; el FE los tiene con prefijo `legal_rep_`. Sin
  // estos alias, un `next_step` a media identidad caía al primer step del
  // bloque (`legal_rep_consent`) y reiniciaba la captura ya hecha.
  //
  // `identity_reuse_offer` y `location_and_camera_admin` no tienen pantalla
  // propia: la oferta de reuso es un CTA dentro de LegalRepConsent y los
  // permisos se piden ahí mismo, así que ambos resuelven al consentimiento.
  identity_reuse_offer: 'legal_rep_consent',
  location_and_camera_admin: 'legal_rep_consent',
  ine_capture_admin: 'legal_rep_ine_front',
  selfie_admin: 'legal_rep_selfie',
  // La confirmación de datos del representante vive en su propio bloque FE
  // (legal_rep_data), fuera de legal_rep_kyc. Apunta al DOMICILIO y no a
  // `legal_rep_data`: quien envía `kyc/confirm-data` —lo que cierra el step—
  // es la pantalla de domicilio.
  data_confirmation_admin: 'legal_rep_address',
  // bloque closing
  privacy_notice_acceptance: 'privacy_notice',
  contracts_signature_pm: 'contracts_pm',
};

/**
 * Bloque → primer step FE del bloque (fallback de `mapBackendStep` cuando el
 * step no matchea). Acepta tanto los nombres de bloque FE como los nombres /
 * aasm_state reales del BE (`kyb_validation`, `closing`, `signing`, …).
 */
const BLOCK_TO_FIRST_STEP: Record<string, string> = {
  // Nombres de bloque FE
  account_access: 'access',
  company_docs: 'company_docs',
  company_data: 'company_data',
  legal_rep_kyc: 'legal_rep_consent',
  legal_rep_data: 'legal_rep_data',
  pep_declaration: 'pep_declaration',
  // El bloque del BE resuelve a la pantalla de identificación: es la única
  // que captura propietarios reales en PM.
  beneficial_owners: 'beneficial_owner_identity',
  origin_of_funds: 'origin_of_funds',
  // Ver el alias de arriba: el bloque ya no tiene pantalla propia.
  company_address_verify: 'legal_review_wait',
  legal_review: 'legal_review_wait',
  // El bloque closing arranca por el aviso de privacidad (antes de la firma).
  contracts_signature: 'privacy_notice',
  finalize: 'finalize',
  // Nombres de bloque / aasm_state del BE (moral_v1.yml)
  registration_admin: 'access',
  kyc_admin: 'legal_rep_consent',
  // El bloque kyb_validation se quedó sin pantallas propias (2026-08-18): sus
  // dos documentos se entregan en Documentación, así que ahí aterriza.
  kyb_validation: 'company_docs',
  closing: 'privacy_notice',
  signing: 'privacy_notice',
};

export const moralFlow: OnboardingFlow = {
  partyType: 'moral',
  version: 'moral_v1',
  steps,
  stepFor: stepId => byId.get(stepId) ?? null,
  stepForPath: path => byPath.get(path) ?? null,
  defaultOrder: () => steps.map(s => s.id),
  mapBackendStep: backend => {
    // Traducir la clave del BE a id FE (donde difieren) antes de resolver; si el
    // resultado matchea un id FE conocido, usarlo directo. Si no, caer al primer
    // step del bloque (acepta nombres de bloque FE y BE).
    const aliased = BACKEND_STEP_ALIASES[backend.step] ?? backend.step;
    if (byId.has(aliased)) return aliased;
    return BLOCK_TO_FIRST_STEP[backend.block] ?? null;
  },
};

/** URL de fallback PM cuando no hay `next_step` resoluble (sesión recién creada). */
export const MORAL_ACCESS_URL = '/onboarding/access';

/**
 * URLs segmentadas de "Crear acceso" PM (account_access). Cada card tiene su
 * propia ruta (paridad con el registro PF), para recuperación de sesión /
 * deep-link por step. `location` es la ruta base (`/onboarding/access`).
 */
export const MORAL_ACCESS_STEP_URLS = {
  location: MORAL_ACCESS_URL,
  email: `${MORAL_ACCESS_URL}/email`,
  emailVerification: `${MORAL_ACCESS_URL}/email-verification`,
  phone: `${MORAL_ACCESS_URL}/phone`,
  phoneVerification: `${MORAL_ACCESS_URL}/phone-verification`,
  password: `${MORAL_ACCESS_URL}/password`,
} as const;

/**
 * ¿El bloque del BE es el de registro (email/phone/password)? El motor emite
 * `registration_admin` en `moral_v1.yml`, pero las respuestas de los endpoints
 * de registro —compartidos con PF— traen `registration` a secas. Aceptar los
 * dos evita que la recuperación de sesión pierda la card exacta del stack de
 * Crear acceso y reinicie al usuario al principio del bloque.
 */
export function isRegistrationBlock(block: string | undefined): boolean {
  return block === 'registration' || block === 'registration_admin';
}

/**
 * Resolución de recuperación PM: traduce el `next_step` del BE ({block, step})
 * a la URL del step FE. La usa `OnboardingEntry` para aterrizar al usuario PM
 * en su step en curso al recuperar sesión / deep-link, en vez de reiniciarlo
 * siempre en `account_access`.
 *
 * Caso especial: el `account_access` de PM reusa el bloque de registro del BE
 * (email/phone/password, igual que PF), así que esos steps mapean a las
 * sub-URLs segmentadas de Crear acceso. El resto de bloques PM resuelven por
 * `moralFlow`. Fallback a `MORAL_ACCESS_URL` cuando no hay `next_step` o el
 * bloque/step es desconocido — preserva el comportamiento sin romper.
 */
export function resolveMoralStepUrl(
  next: { block: string; step: string } | null | undefined
): string {
  if (next && isRegistrationBlock(next.block)) {
    const step = next.step ?? '';
    if (step.startsWith('email')) return MORAL_ACCESS_STEP_URLS.email;
    if (step.startsWith('phone')) return MORAL_ACCESS_STEP_URLS.phone;
    if (step === 'password') return MORAL_ACCESS_STEP_URLS.password;
    return MORAL_ACCESS_URL;
  }
  const stepId = next ? moralFlow.mapBackendStep(next) : null;
  const path = stepId ? moralFlow.stepFor(stepId)?.path : null;
  return path ? `/onboarding/${path}` : MORAL_ACCESS_URL;
}

/** Orden canónico de los step ids del flujo moral. */
const MORAL_STEP_IDS: readonly string[] = steps.map(s => s.id);

/** Traduce el `next_step` del BE al step id del moralFlow (registro → access). */
function moralStepIdForNext(next: { block: string; step: string }): string | null {
  if (isRegistrationBlock(next.block)) return 'access';
  return moralFlow.mapBackendStep(next);
}

/**
 * Steps que el usuario PM tiene desbloqueados según el `next_step` del BE
 * (server-driven, lo usa el guard de `MoralShell`). Política **generosa**:
 * desbloquea hasta el ÚLTIMO step del bloque en curso (dentro de un bloque
 * multi-step —company_data: data/address/scian; legal_rep_kyc: consent/ine/
 * selfie— todos quedan disponibles; el BE valida cada POST igual). Sin
 * `next_step` (sesión nueva / blocks no disponibles) devuelve TODOS los steps
 * para **no atrapar** al usuario (fail-open).
 */
export function deriveMoralUnlockedSteps(
  next: { block: string; step: string } | null | undefined
): readonly string[] {
  if (!next) return MORAL_STEP_IDS;
  const targetId = moralStepIdForNext(next);
  if (!targetId) return MORAL_STEP_IDS;
  const targetBlock = moralFlow.stepFor(targetId)?.block;
  if (!targetBlock) return MORAL_STEP_IDS;
  // Índice del último step del bloque en curso → techo de desbloqueo.
  let ceilingIdx = MORAL_STEP_IDS.indexOf(targetId);
  for (let i = MORAL_STEP_IDS.length - 1; i >= 0; i--) {
    if (moralFlow.stepFor(MORAL_STEP_IDS[i]!)?.block === targetBlock) {
      ceilingIdx = Math.max(ceilingIdx, i);
      break;
    }
  }
  return MORAL_STEP_IDS.slice(0, ceilingIdx + 1);
}

/**
 * ¿El step `stepId` ya fue completado según el BE? Un step está completado si su
 * posición en el orden canónico es **estrictamente anterior** al step en curso
 * (`next_step`). A diferencia de `deriveMoralCompletedSteps` (que razona por
 * bloque), aquí la granularidad es por-step: dentro de un bloque multi-step
 * (company_data: data/address/scian) distingue cuáles sub-steps ya se enviaron.
 *
 * Habilita el **bypass** (paridad con PF `OnboardingShell.handleNextStep`): al
 * avanzar sobre un step ya enviado, el form no re-hace el POST, sólo navega al
 * siguiente. Sin `next_step` (sesión nueva / blocks no disponibles) → `false`:
 * el form envía normal (fail-safe; nunca saltamos un paso sin que el BE lo
 * confirme como pasado).
 */
export function isMoralStepCompleted(
  next: { block: string; step: string } | null | undefined,
  stepId: string
): boolean {
  if (!next) return false;
  const targetId = moralStepIdForNext(next);
  if (!targetId) return false;
  const stepIdx = MORAL_STEP_IDS.indexOf(stepId);
  const targetIdx = MORAL_STEP_IDS.indexOf(targetId);
  if (stepIdx < 0 || targetIdx < 0) return false;
  return stepIdx < targetIdx;
}

/**
 * Estado por step que reporta el BE en `blocks` (blocks_status de la sesión).
 * Los nombres son los de `moral_v1.yml`, no los ids FE.
 */
export type MoralBackendBlocks = Record<
  string,
  {
    status: string;
    /**
     * `required` lo manda el motor por step y NO es redundante con el status:
     * en `kyb_validation` cinco steps son opcionales (estructura accionaria,
     * organigrama, propietario real, beneficiarios y PEP), así que un rango
     * puede estar terminado aunque varios sigan en `pending`. Lo usa
     * `deriveMoralSealedSteps` para no esperar por algo que nadie exige.
     */
    steps?: Record<string, { status: string; required?: boolean }>;
  }
>;

/**
 * Qué step(s) del BE respaldan cada step FE. Sirve para decidir el **bypass**
 * con el estado real que reporta el motor (`blocks_status`), no por posición:
 * el orden de pantallas del FE (2026-08-18: empresa antes del KYC) no coincide
 * con el orden de bloques de `moral_v1.yml` (kyc_admin antes de company_data),
 * así que "está antes del `next_step`" NO significa "ya se envió". Con
 * `next_step` en kyc_admin, por posición se daban por hechos documentación,
 * estructura, organigrama, propietario real, SCIAN y datos de la empresa, y
 * los forms avanzaban sin POST.
 *
 * `mode: 'all'` exige todos los steps listados; `'any'`, con uno basta (cuando
 * el mismo envío puede quedar registrado bajo más de una clave).
 */
const FE_STEP_BACKEND_EVIDENCE: Record<
  string,
  { block: string; steps: readonly string[]; mode?: 'all' | 'any' }[]
> = {
  // El card stack de acceso cierra los cinco steps de `registration_admin`.
  // No lo consume ningún bypass —la pantalla gobierna sus cards con el estado
  // de la sesión— pero sí `deriveMoralSealedSteps`: sin esta entrada, el
  // acceso no tendría evidencia mapeada y VOLVER seguiría alcanzándolo aunque
  // el correo y el teléfono ya estuvieran verificados por OTP.
  access: [
    {
      block: 'registration_admin',
      steps: ['email', 'email_verification', 'phone', 'phone_verification', 'password'],
    },
  ],
  company_docs: [
    { block: 'company_data', steps: ['company_csf', 'comprobante_domicilio_pm'] },
    { block: 'kyb_validation', steps: ['acta_constitutiva'] },
  ],
  company_structure: [{ block: 'kyb_validation', steps: ['estructura_accionaria'] }],
  org_structure: [{ block: 'kyb_validation', steps: ['organigrama'] }],
  beneficial_owner_identity: [{ block: 'kyb_validation', steps: ['propietario_real'] }],
  // El giro viaja dentro de company/data: cuenta como enviado cuando los datos
  // de la empresa ya están registrados.
  scian: [
    { block: 'company_data', steps: ['rfc_homoclave', 'company_data_confirmation'], mode: 'any' },
  ],
  company_data: [
    { block: 'company_data', steps: ['rfc_homoclave', 'company_data_confirmation'], mode: 'any' },
  ],
  company_address: [{ block: 'company_data', steps: ['company_address'] }],
  origin_of_funds: [
    {
      block: 'kyb_validation',
      steps: ['origin_of_funds', 'transactional_profile_pm'],
      mode: 'any',
    },
  ],
  legal_rep_consent: [{ block: 'kyc_admin', steps: ['location_and_camera_admin'] }],
  legal_rep_ine_front: [{ block: 'kyc_admin', steps: ['ine_capture_admin'] }],
  legal_rep_selfie: [{ block: 'kyc_admin', steps: ['selfie_admin'] }],
  // `legal_rep_data` (poder y política de firma) NO tiene step propio en el
  // contrato del BE: su POST va a `legal-representatives`, que no marca nada en
  // los blocks. Se apoya en el paso anterior para saber que ya se pasó por él.
  legal_rep_data: [{ block: 'kyc_admin', steps: ['selfie_admin'] }],
  legal_rep_address: [{ block: 'kyc_admin', steps: ['data_confirmation_admin'] }],
  pep_declaration: [{ block: 'kyb_validation', steps: ['pep_declaration_pm'] }],
  legal_review_wait: [{ block: 'kyb_validation', steps: ['legal_review_wait'] }],
  privacy_notice: [{ block: 'closing', steps: ['privacy_notice_acceptance'] }],
  contracts_pm: [{ block: 'closing', steps: ['contracts_signature_pm'] }],
};

const DONE_STATUSES = new Set(['completed', 'not_required', 'skipped']);

/**
 * ¿El BE ya tiene registrado el step FE `stepId`? Lee `blocks` (estado real).
 * Devuelve `null` cuando no se puede saber —sin `blocks`, step sin evidencia
 * mapeada o bloque ausente en la respuesta— para que el caller caiga al
 * criterio anterior (posición vs `next_step`) en vez de inventar un valor.
 */
export function isMoralStepCompletedByBlocks(
  blocks: MoralBackendBlocks | null | undefined,
  stepId: string
): boolean | null {
  if (!blocks) return null;
  const evidence = FE_STEP_BACKEND_EVIDENCE[stepId];
  if (!evidence) return null;

  let known = false;
  for (const { block, steps: keys, mode = 'all' } of evidence) {
    const be = blocks[block];
    if (!be) continue;
    known = true;
    // Bloque cerrado por el motor: todo lo suyo cuenta como enviado.
    if (DONE_STATUSES.has(be.status)) continue;
    const statuses = keys.map(k => be.steps?.[k]?.status);
    const done = statuses.map(st => (st ? DONE_STATUSES.has(st) : false));
    const ok = mode === 'any' ? done.some(Boolean) : done.every(Boolean);
    if (!ok) return false;
  }
  return known ? true : null;
}

/**
 * Primer y último step del TRAMO DE EMPRESA, que se sella como una sola
 * unidad. Va de Documentación a Perfil transaccional.
 */
const COMPANY_SEGMENT_BOUNDS = ['company_docs', 'origin_of_funds'] as const;

/** Los steps FE del tramo de empresa, en el orden del flujo. */
const COMPANY_SEGMENT_STEPS: readonly string[] = (() => {
  const from = MORAL_STEP_IDS.indexOf(COMPANY_SEGMENT_BOUNDS[0]);
  const to = MORAL_STEP_IDS.indexOf(COMPANY_SEGMENT_BOUNDS[1]);
  return from >= 0 && to >= from ? MORAL_STEP_IDS.slice(from, to + 1) : [];
})();

/**
 * ¿Están cerrados todos los steps del BE que este tramo exige?
 *
 * Sólo cuentan los `required`: el motor marca cinco steps del tramo como
 * opcionales —estructura accionaria, organigrama, propietario real,
 * beneficiarios y PEP— y quedarían en `pending` para siempre si el usuario no
 * pasa por ellos. Esperarlos dejaría la regla inerte.
 *
 * Se leen los STEPS, nunca el `status` del bloque: un bloque puede figurar
 * cerrado con opcionales pendientes, y ahí el atajo sellaría de más.
 */
function isCompanySegmentDone(blocks: MoralBackendBlocks): boolean {
  let sawEvidence = false;

  for (const stepId of COMPANY_SEGMENT_STEPS) {
    for (const { block, steps: keys, mode = 'all' } of FE_STEP_BACKEND_EVIDENCE[stepId] ?? []) {
      const be = blocks[block];
      if (!be?.steps) continue;

      const required = keys.filter(k => be.steps?.[k]?.required !== false);
      if (required.length === 0) continue;
      sawEvidence = true;

      const done = required.map(k => {
        const st = be.steps?.[k]?.status;
        return st ? DONE_STATUSES.has(st) : false;
      });
      const ok = mode === 'any' ? done.some(Boolean) : done.every(Boolean);
      if (!ok) return false;
    }
  }

  // Sin un solo step conocido no se afirma que el tramo esté hecho.
  return sawEvidence;
}

/**
 * Steps que VOLVER ya no puede alcanzar. Un step sellado deja de ser destino
 * de la navegación hacia atrás.
 *
 * Se sella con DOS criterios, porque las pantallas y los bloques del BE no se
 * corresponden uno a uno:
 *
 *  1. **El acceso, por bloque.** `registration_admin` es el único bloque limpio
 *     del flujo: sus cinco steps son obligatorios y viven todos en la misma
 *     pantalla. Cerrado el bloque, no queda nada que hacer ahí —el correo y el
 *     teléfono están verificados por OTP—, así que se sella.
 *
 *  2. **El tramo de empresa, como una unidad.** De Documentación a Perfil
 *     transaccional se sella entero o no se sella nada. Sellar por bloque ahí
 *     no funciona: `company_data` y `kyb_validation` se INTERCALAN sobre esas
 *     ocho pantallas —`/company-docs` alimenta los dos a la vez—, así que
 *     cerrar un bloque sellaría pantallas sueltas de en medio y dejaría
 *     agujeros por los que VOLVER saltaría hacia atrás de más.
 *
 * Sin `blocks` no sella nada (fail-open): sin evidencia no se le quita al
 * usuario una salida que ya tenía.
 *
 * ⚠️ **Sólo gobierna el botón VOLVER, no el acceso por URL.** Entrar tecleando
 * la ruta —o con el atrás del navegador— a un step sellado sigue funcionando.
 * Cerrar esa puerta pide un guard que redirige, y se dejó fuera a propósito
 * (2026-08-25): un guard mal puesto rebota al usuario a media captura, y ya hay
 * precedente de ese riesgo en el refetch de `MoralShell`. Ocultar un botón de
 * más no hace daño; expulsar de una pantalla, sí.
 */
export function deriveMoralSealedSteps(
  blocks: MoralBackendBlocks | null | undefined
): readonly string[] {
  if (!blocks) return [];
  const sealed: string[] = [];

  // `registration` es el alias que reporta el motor para las empresas migradas
  // (RFC 0030 §14): la cuenta la activó una invitación, no el flujo de alta, y
  // el bloque de acceso llega cerrado con ese nombre. Mirar sólo
  // `registration_admin` dejaba las cards de "Crear acceso" abiertas para ellas.
  if (
    DONE_STATUSES.has(blocks.registration_admin?.status ?? '') ||
    DONE_STATUSES.has(blocks.registration?.status ?? '')
  ) {
    sealed.push('access');
  }

  if (isCompanySegmentDone(blocks)) {
    sealed.push(...COMPANY_SEGMENT_STEPS);
  }

  return sealed;
}

/**
 * Steps de bloques ANTERIORES al bloque en curso → considerados completados
 * por el BE. Útil para marcar cards/stepper. Sin `next_step` devuelve `[]`.
 */
export function deriveMoralCompletedSteps(
  next: { block: string; step: string } | null | undefined
): readonly string[] {
  if (!next) return [];
  const targetId = moralStepIdForNext(next);
  if (!targetId) return [];
  const targetBlock = moralFlow.stepFor(targetId)?.block;
  if (!targetBlock) return [];
  const firstIdxOfBlock = MORAL_STEP_IDS.findIndex(
    id => moralFlow.stepFor(id)?.block === targetBlock
  );
  return firstIdxOfBlock <= 0 ? [] : MORAL_STEP_IDS.slice(0, firstIdxOfBlock);
}

/**
 * A dónde mandar al usuario PM que cae en un step inalcanzable — el destino del
 * rebote del guard y del aterrizaje de `OnboardingEntry`.
 *
 * Recorre el ORDEN DEL FE y devuelve el primer step que sigue pendiente. Esa es
 * la diferencia con `resolveMoralStepUrl`, que traduce el `next_step` del BE:
 * los bloques del BE van en otro orden (`moral_v1.yml` pone `kyc_admin` antes
 * que `company_data`), así que traducirlo a pelo mandaba al KYC del
 * representante a quien acababa de cerrar el registro, saltándose todo el tramo
 * de empresa. Aquí el único orden que se consulta es el de `steps`; del BE sólo
 * se lee EVIDENCIA (`blocks`: qué se envió), que es neutral al orden.
 *
 * Un step se salta SÓLO con evidencia afirmativa de que ya se pasó por él: o el
 * BE lo reporta enviado (`isMoralStepCompletedByBlocks === true`) o está
 * sellado. El `null` —bloque ausente en la respuesta, step sin evidencia
 * mapeada— cuenta como pendiente y detiene el recorrido: ante la duda se
 * aterriza ANTES, que es recuperable, y no después, que se salta captura.
 *
 * Fallback a `resolveMoralStepUrl(next)` cuando no hay `blocks` —el 401 conocido
 * de PM en dev— o cuando todo el flujo ya tiene evidencia: ahí el `next_step`
 * del BE es lo único que queda, y es el comportamiento previo.
 */
export function resolveMoralResumeUrl(
  next: { block: string; step: string } | null | undefined,
  blocks: MoralBackendBlocks | null | undefined
): string {
  if (!blocks) return resolveMoralStepUrl(next);

  const sealed = new Set(deriveMoralSealedSteps(blocks));
  for (const stepId of MORAL_STEP_IDS) {
    if (sealed.has(stepId)) continue;
    // Sólo la evidencia afirmativa (`true`) salta el step; `null` es duda y la
    // duda se resuelve aterrizando aquí.
    if (isMoralStepCompletedByBlocks(blocks, stepId) === true) continue;
    // `access` es el único step FE con sub-rutas propias (correo / teléfono /
    // contraseña). Devolver su path base manda al usuario al principio del
    // stack: la card activa sale de la URL, así que una sesión a la que sólo
    // le falta la contraseña aterrizaba con el foco en Ubicación y la card de
    // contraseña bloqueada. Cuando el `next_step` del BE está en el bloque de
    // registro, él sabe la sub-ruta exacta y manda.
    if (stepId === 'access' && isRegistrationBlock(next?.block)) {
      return resolveMoralStepUrl(next);
    }
    const path = moralFlow.stepFor(stepId)?.path;
    if (path) return `/onboarding/${path}`;
  }

  return resolveMoralStepUrl(next);
}
